import type { v2 } from '@deque/billing-service-client';
import {
  Checkbox,
  DescriptionDetails,
  DescriptionList,
  DescriptionListItem,
  DescriptionTerm,
  IconButton,
  Offscreen,
  OptionsMenu,
  OptionsMenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TooltipTabstop
} from '@deque/cauldron-react';
import classNames from 'classnames';
import React, { useMemo, useRef, useState, type JSX } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { type TransInterpolation } from '~/common/types';

import { getAccessString } from '~/app/utils/access-utils';
import HighlightText from '~/common/components/HighlightText';
import { type ProductSlugs } from '~/common/constants';
import {
  memberHasAccepted,
  memberIsPending,
  type EnterpriseMembers,
  type ProductAccess
} from '~/common/utils/billing-client/client-v2';
import { useFeatureFlagState } from '~/modules/common/contexts/featureFlags';
import { formatLongDateTime } from '~/modules/common/utils/dates';
import styles from './UserManagementTable.css';

export interface UserManagementTableProps {
  filteredMembers: EnterpriseMembers.PendingOrAcceptedMember[];
  currentUserId: string;
  getProductBySlug: (slug: string) => v2.Product | undefined;
  onResendClick: (member: EnterpriseMembers.PendingMember) => void;
  onRemoveClick: (member: EnterpriseMembers.PendingOrAcceptedMember) => void;
  isMobile?: boolean;
  membersPerPage?: number;
  hiddenProductSlugs: ProductSlugs[];
  isMailerEnabled: boolean;
  searchQuery?: string;
  selectedMembers: EnterpriseMembers.PendingOrAcceptedMember[];
  setSelectedMembers: React.Dispatch<
    React.SetStateAction<EnterpriseMembers.PendingOrAcceptedMember[]>
  >;
  setShowAcceptedMemberDetails: (
    member: EnterpriseMembers.AcceptedMember
  ) => void;
  setShowPendingMemberDetails: (
    member: EnterpriseMembers.PendingMember
  ) => void;
}

type SortBy = null | 'name' | 'email' | 'status' | 'lastActive';
type SortDirection = 'none' | 'ascending' | 'descending';

const UserManagementTable = ({
  filteredMembers,
  searchQuery,
  currentUserId,
  onResendClick,
  onRemoveClick,
  getProductBySlug,
  isMobile = false,
  membersPerPage = 50,
  hiddenProductSlugs,
  isMailerEnabled,
  selectedMembers,
  setSelectedMembers,
  setShowAcceptedMemberDetails,
  setShowPendingMemberDetails
}: UserManagementTableProps): JSX.Element => {
  const { t } = useTranslation();
  const userActivityEnabled = useFeatureFlagState('user_activity_v1');
  const enterpriseEmailRestrictionEnabled = useFeatureFlagState(
    'restrict_enterprise_email_changes_v1'
  );

  const [sort, setSort] = useState<[SortBy, SortDirection]>([null, 'none']);
  const [sortBy, sortDirection] = sort;
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedProductAccess, setExpandedProductAccess] = useState<
    string | null
  >(null);
  const productAccessPortals = useRef<Record<string, HTMLElement | null>>({});

  const totalMembers = filteredMembers.length;
  const memberStart = currentPage * membersPerPage - membersPerPage + 1;
  const memberEnd = Math.min(memberStart + membersPerPage - 1, totalMembers);

  const onNextPageClick = () => setCurrentPage(currentPage + 1);
  const onFirstPageClick = () => setCurrentPage(1);
  const onPreviousPageClick = () => setCurrentPage(currentPage - 1);
  const onLastPageClick = () =>
    setCurrentPage(Math.ceil(totalMembers / membersPerPage));

  // default sorting
  const sortByDefault = (
    data: EnterpriseMembers.PendingOrAcceptedMember[]
  ): EnterpriseMembers.PendingOrAcceptedMember[] =>
    data?.sort((a, b) => {
      if (memberHasAccepted(a) && memberIsPending(b)) {
        return 1;
      }
      if (memberIsPending(a) && memberHasAccepted(b)) {
        return -1;
      }
      return a.email.localeCompare(b.email);
    });

  const sortByName = (
    data: EnterpriseMembers.PendingOrAcceptedMember[]
  ): EnterpriseMembers.PendingOrAcceptedMember[] =>
    data.sort((a, b) => {
      if (memberHasAccepted(a) && memberIsPending(b)) {
        return sortDirection === 'ascending' ? 1 : -1;
      } else if (memberIsPending(a) && memberHasAccepted(b)) {
        return sortDirection === 'ascending' ? -1 : 1;
      } else if (memberHasAccepted(a) && memberHasAccepted(b)) {
        return sortDirection === 'ascending'
          ? `${a.first_name} ${a.last_name}`.localeCompare(
              `${b.first_name} ${b.last_name}`
            )
          : `${b.first_name} ${b.last_name}`.localeCompare(
              `${a.first_name} ${a.last_name}`
            );
      }
      return 0;
    });

  const sortByEmail = (
    data: EnterpriseMembers.PendingOrAcceptedMember[]
  ): EnterpriseMembers.PendingOrAcceptedMember[] =>
    data.sort((a, b) =>
      (sortDirection === 'ascending' ? a.email : b.email).localeCompare(
        sortDirection === 'ascending' ? b.email : a.email
      )
    );

  const sortByStatus = (
    data: EnterpriseMembers.PendingOrAcceptedMember[]
  ): EnterpriseMembers.PendingOrAcceptedMember[] =>
    data.sort((a, b) => {
      if (memberHasAccepted(a) && memberIsPending(b)) {
        return sortDirection === 'ascending' ? 1 : -1;
      }
      if (memberIsPending(a) && memberHasAccepted(b)) {
        return sortDirection === 'ascending' ? -1 : 1;
      }
      return 0;
    });

  type ActivityCategory = 'pending' | 'noActivity' | 'hasDate';

  const getActivityCategory = (
    member: EnterpriseMembers.PendingOrAcceptedMember
  ): ActivityCategory => {
    if (!memberHasAccepted(member)) {
      return 'pending';
    }
    if (member.user_activity.length === 0) {
      return 'noActivity';
    }
    return 'hasDate';
  };

  const getLatestActivity = (
    member: EnterpriseMembers.PendingOrAcceptedMember
  ): string => {
    if (!memberHasAccepted(member) || member.user_activity.length === 0) {
      return '';
    }
    const dates = member.user_activity.map(a => a.updated_at);
    dates.sort();
    return dates[dates.length - 1];
  };

  const sortByLastActive = (
    data: EnterpriseMembers.PendingOrAcceptedMember[]
  ): EnterpriseMembers.PendingOrAcceptedMember[] => {
    // Per UX: ascending = invite pending → no activity → oldest → newest;
    // descending = newest → oldest → no activity → invite pending.
    const groupOrder: Record<ActivityCategory, number> =
      sortDirection === 'ascending'
        ? { pending: 0, noActivity: 1, hasDate: 2 }
        : { hasDate: 0, noActivity: 1, pending: 2 };

    return data.sort((a, b) => {
      const aCategory = getActivityCategory(a);
      const bCategory = getActivityCategory(b);
      if (aCategory !== bCategory) {
        return groupOrder[aCategory] - groupOrder[bCategory];
      }
      if (aCategory !== 'hasDate') {
        return 0;
      }
      const aDate = getLatestActivity(a);
      const bDate = getLatestActivity(b);
      return sortDirection === 'ascending'
        ? aDate.localeCompare(bDate)
        : bDate.localeCompare(aDate);
    });
  };

  const handleSort = (
    data: EnterpriseMembers.PendingOrAcceptedMember[]
  ): EnterpriseMembers.PendingOrAcceptedMember[] => {
    if (!sortBy) {
      return sortByDefault(data);
    }
    switch (sortBy) {
      case 'email':
        return sortByEmail(data);
      case 'name':
        return sortByName(data);
      case 'lastActive':
        return sortByLastActive(data);
      case 'status':
      default:
        return sortByStatus(data);
    }
  };

  const sortedUsers = handleSort(filteredMembers);
  const getCurrentSortDirection = (column: string) =>
    column === sortBy ? sortDirection : 'none';
  const getNextSortDirection = (column: string) =>
    column === sortBy && sortDirection === 'ascending'
      ? 'descending'
      : 'ascending';

  const paginatedUsers = sortedUsers.slice(memberStart - 1, memberEnd);

  const isAdmin = (is_admin: boolean) =>
    is_admin ? t('Admin') : t('General Access');

  const getProductAccess = (access: ProductAccess): string | undefined => {
    const product = getProductBySlug(access.product_slug);
    if (!product) {
      return;
    }
    return t('{{ productName }}: {{accessType}}', {
      productName: product.name,
      accessType: getAccessString(t, access.access_type)
    });
  };

  const pendingMemberOptions = (member: EnterpriseMembers.PendingMember) => (
    <OptionsMenu
      className={styles.optionsMenu}
      align={isMobile ? 'left' : 'right'}
      trigger={triggerProps => (
        <IconButton
          icon="kabob"
          label={
            <Trans>
              More Options
              <Offscreen>
                {' '}
                for {{ memberEmail: member.email } as TransInterpolation}
              </Offscreen>
            </Trans>
          }
          tooltipProps={{ variant: 'info' }}
          className={styles.optionsButton}
          {...triggerProps}
        />
      )}
    >
      <OptionsMenuItem
        onSelect={() => onResendClick(member)}
        disabled={!isMailerEnabled}
        className={styles.optionsMenuItem}
      >
        <Trans>
          Resend invitation
          <Offscreen>
            {' '}
            to {{ memberEmail: member.email } as TransInterpolation}
          </Offscreen>
        </Trans>
      </OptionsMenuItem>
      {enterpriseEmailRestrictionEnabled && (
        <OptionsMenuItem
          onSelect={() => undefined}
          disabled
          className={styles.optionsMenuItem}
        >
          <Trans>
            Edit email
            <Offscreen>
              {' '}
              for {{ memberEmail: member.email } as TransInterpolation}
            </Offscreen>
          </Trans>
        </OptionsMenuItem>
      )}
      <OptionsMenuItem
        onSelect={() => onRemoveClick(member)}
        className={styles.optionsMenuItem}
      >
        <Trans>
          Remove member
          <Offscreen>
            {' '}
            {{ memberEmail: member.email } as TransInterpolation}
          </Offscreen>
        </Trans>
      </OptionsMenuItem>
    </OptionsMenu>
  );

  const actionIcons = (
    member: EnterpriseMembers.PendingOrAcceptedMember
  ): JSX.Element => (
    <div className={styles.actionIcons}>
      <IconButton
        tooltipProps={{ variant: 'info' }}
        icon="eye"
        label={
          <>
            {t('View Details')}
            <Offscreen> {member.email}</Offscreen>
          </>
        }
        onClick={() => {
          if (memberHasAccepted(member)) {
            setShowAcceptedMemberDetails(member);
          } else {
            setShowPendingMemberDetails(member);
          }
        }}
      />
      {memberHasAccepted(member) ? (
        <>
          <IconButton
            tooltipProps={{ variant: 'info' }}
            icon="pencil"
            as={Link}
            to={`/user-access/edit-user/${member.user_id}`}
            label={
              <>
                {t('Edit Access')}
                <Offscreen> {member.email}</Offscreen>
              </>
            }
          />
          {member.user_id !== currentUserId && (
            <IconButton
              tooltipProps={{ variant: 'info' }}
              icon="minus"
              label={
                <>
                  {t('Remove Member')}
                  <Offscreen> {member.email}</Offscreen>
                </>
              }
              onClick={() => onRemoveClick(member)}
            />
          )}
        </>
      ) : (
        pendingMemberOptions(member)
      )}
    </div>
  );

  const getMemberDetails = (
    member: EnterpriseMembers.PendingOrAcceptedMember
  ) => {
    const productAccess = member.product_access.filter(access => {
      return (
        !hiddenProductSlugs.includes(access.product_slug as ProductSlugs) &&
        !!getProductBySlug(access.product_slug)
      );
    });

    const memberName = (
      <HighlightText
        text={
          memberHasAccepted(member)
            ? `${member.first_name} ${member.last_name}`
            : '--'
        }
        highlight={searchQuery}
      />
    );

    const memberEmail = (
      <HighlightText text={member.email} highlight={searchQuery} />
    );

    const uniqueKey =
      'user_id' in member ? member.user_id : member.invitation_id;

    // +1 to account for Axe Account access
    const productCount = productAccess.length + 1;
    const productCountLabel = t(
      productCount === 1
        ? '{{count}} product; show product list for {{memberEmail}}'
        : '{{count}} products; show product list for {{memberEmail}}',
      {
        count: productCount,
        memberEmail: member.email
      }
    );

    const productAccessTooltip = (
      <>
        <TooltipTabstop
          placement="top"
          show={expandedProductAccess === uniqueKey}
          portal={productAccessPortals.current[uniqueKey] ?? undefined}
          aria-disabled={false}
          aria-expanded={expandedProductAccess === uniqueKey}
          aria-controls={`product-access-${uniqueKey}`}
          aria-label={productCountLabel}
          onClick={() =>
            setExpandedProductAccess(current =>
              current === uniqueKey ? null : uniqueKey
            )
          }
          className={styles.productAccessCount}
          tooltip={
            <ul
              id={`product-access-${uniqueKey}`}
              className={styles.productAccessList}
            >
              <li>
                Axe {t('Account')}: {isAdmin(member.is_admin)}
              </li>
              {productAccess.map(access => (
                <li key={`${access.product_slug}-${uniqueKey}`}>
                  {getProductAccess(access)}
                </li>
              ))}
            </ul>
          }
        >
          {productCount}
        </TooltipTabstop>
        <div
          ref={element => {
            productAccessPortals.current[uniqueKey] = element;
          }}
        />
      </>
    );

    const memberStatus = memberIsPending(member)
      ? t('Email Sent')
      : t('Active');

    const memberActions = actionIcons(member);

    let userActivity: string;

    if (!memberHasAccepted(member)) {
      userActivity = t('Invite pending');
    } else if (member.user_activity.length === 0) {
      userActivity = t('No activity');
    } else {
      const dates = member.user_activity.map(a => a.updated_at);
      dates.sort();
      const mostRecent = new Date(dates[dates.length - 1]);
      userActivity = formatLongDateTime(mostRecent);
    }

    return {
      key: uniqueKey,
      memberName,
      memberEmail,
      productAccessTooltip,
      memberStatus,
      memberActions,
      userActivity
    };
  };

  const memberDetails = useMemo(
    () => paginatedUsers.map(member => getMemberDetails(member)),
    [paginatedUsers, expandedProductAccess]
  );

  const handleSelect = (
    memberToToggle: EnterpriseMembers.PendingOrAcceptedMember
  ) => {
    setSelectedMembers(prev => {
      if (prev.some(m => m.email === memberToToggle.email)) {
        return prev.filter(m => m.email !== memberToToggle.email);
      }
      return [...prev, memberToToggle];
    });
  };

  const handleSelectAllOnPage = () => {
    setSelectedMembers(prev => {
      const selectedEmails = new Set(prev.map(m => m.email));
      const usersToAdd = paginatedUsers.filter(
        p => !selectedEmails.has(p.email)
      );
      return [...prev, ...usersToAdd];
    });
  };

  const handleDeselectAllOnPage = () => {
    setSelectedMembers(prev => {
      const pageEmails = new Set(paginatedUsers.map(p => p.email));
      return prev.filter(m => !pageEmails.has(m.email));
    });
  };

  return (
    <div
      className={classNames(styles.userManagementBody, {
        [styles.userManagementBodyMobile]: isMobile
      })}
    >
      {!isMobile ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader scope="col">
                <Checkbox
                  id="user-management-table-select-all"
                  label={
                    <Offscreen>
                      {t('Select all members on this page')}
                    </Offscreen>
                  }
                  checked={
                    paginatedUsers.length > 0 &&
                    paginatedUsers.every(p =>
                      selectedMembers.some(s => s.email === p.email)
                    )
                  }
                  indeterminate={
                    paginatedUsers.length > 0 &&
                    !paginatedUsers.every(p =>
                      selectedMembers.some(s => s.email === p.email)
                    ) &&
                    paginatedUsers.some(p =>
                      selectedMembers.some(s => s.email === p.email)
                    )
                  }
                  onChange={() => {
                    const allOnPageSelected = paginatedUsers.every(p =>
                      selectedMembers.some(s => s.email === p.email)
                    );
                    if (allOnPageSelected) {
                      handleDeselectAllOnPage();
                    } else {
                      handleSelectAllOnPage();
                    }
                  }}
                />
              </TableHeader>
              <TableHeader
                scope="col"
                sortDirection={getCurrentSortDirection('name')}
                onSort={() => {
                  setSort(['name', getNextSortDirection('name')]);
                }}
              >
                {t('Name')}
              </TableHeader>
              <TableHeader
                scope="col"
                sortDirection={getCurrentSortDirection('email')}
                onSort={() => {
                  setSort(['email', getNextSortDirection('email')]);
                }}
              >
                {t('Email')}
              </TableHeader>
              <TableHeader scope="col">{t('Products')}</TableHeader>
              {userActivityEnabled ? (
                <TableHeader
                  scope="col"
                  sortDirection={getCurrentSortDirection('lastActive')}
                  onSort={() => {
                    setSort(['lastActive', getNextSortDirection('lastActive')]);
                  }}
                >
                  {t('Last Active')}
                </TableHeader>
              ) : (
                <TableHeader
                  scope="col"
                  sortDirection={getCurrentSortDirection('status')}
                  onSort={() => {
                    setSort(['status', getNextSortDirection('status')]);
                  }}
                >
                  {t('Status')}
                </TableHeader>
              )}
              <TableHeader scope="col">{t('Actions')}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {memberDetails.map(
              ({
                key,
                memberName,
                memberEmail,
                productAccessTooltip,
                memberStatus,
                memberActions,
                userActivity
              }) => {
                const memberEmailText = memberEmail.props.text;
                const isSelected = selectedMembers.some(
                  m => m.email === memberEmailText
                );

                return (
                  <TableRow key={`${key}-tr`}>
                    <TableCell>
                      <Checkbox
                        id={`${key}-checkbox`}
                        checked={isSelected}
                        label={
                          <Offscreen>
                            {t('Select {{ memberEmail }}', {
                              memberEmail: memberEmailText
                            })}
                          </Offscreen>
                        }
                        onChange={() => {
                          const member = paginatedUsers.find(
                            m => m.email === memberEmailText
                          );

                          if (member) {
                            handleSelect(member);
                          }
                        }}
                      />
                    </TableCell>
                    <TableHeader scope="row">{memberName}</TableHeader>
                    <TableCell>{memberEmail}</TableCell>
                    <TableCell>{productAccessTooltip}</TableCell>
                    <TableCell>
                      {userActivityEnabled ? userActivity : memberStatus}
                    </TableCell>
                    <TableCell>{memberActions}</TableCell>
                  </TableRow>
                );
              }
            )}
          </TableBody>
        </Table>
      ) : (
        <div className={styles.mobileUserList}>
          {memberDetails.map(
            ({
              key,
              memberName,
              memberEmail,
              productAccessTooltip,
              memberStatus,
              memberActions,
              userActivity
            }) => {
              const memberEmailText = memberEmail.props.text;
              const isSelected = selectedMembers.some(
                m => m.email === memberEmailText
              );

              return (
                <div key={key} className={styles.mobileUserListItem}>
                  <Checkbox
                    id={`${key}-checkbox`}
                    checked={isSelected}
                    label={
                      <Offscreen>
                        {t('Select {{ memberEmail }}', {
                          memberEmail: memberEmailText
                        })}
                      </Offscreen>
                    }
                    onChange={() => {
                      const member = paginatedUsers.find(
                        m => m.email === memberEmailText
                      );

                      if (member) {
                        handleSelect(member);
                      }
                    }}
                  />
                  <DescriptionList collapsed>
                    <DescriptionListItem>
                      <DescriptionTerm>{t('Name')}</DescriptionTerm>
                      <DescriptionDetails>{memberName}</DescriptionDetails>
                    </DescriptionListItem>
                    <DescriptionListItem>
                      <DescriptionTerm>{t('Email')}</DescriptionTerm>
                      <DescriptionDetails>{memberEmail}</DescriptionDetails>
                    </DescriptionListItem>
                    <DescriptionListItem>
                      <DescriptionTerm>{t('Products')}</DescriptionTerm>
                      <DescriptionDetails>
                        {productAccessTooltip}
                      </DescriptionDetails>
                    </DescriptionListItem>
                    {userActivityEnabled ? (
                      <DescriptionListItem>
                        <DescriptionTerm>{t('Last Active')}</DescriptionTerm>
                        <DescriptionDetails>{userActivity}</DescriptionDetails>
                      </DescriptionListItem>
                    ) : (
                      <DescriptionListItem>
                        <DescriptionTerm>{t('Status')}</DescriptionTerm>
                        <DescriptionDetails>{memberStatus}</DescriptionDetails>
                      </DescriptionListItem>
                    )}
                    <DescriptionListItem>
                      <DescriptionTerm>{t('Actions')}</DescriptionTerm>
                      <DescriptionDetails>{memberActions}</DescriptionDetails>
                    </DescriptionListItem>
                  </DescriptionList>
                </div>
              );
            }
          )}
        </div>
      )}

      <Pagination
        totalItems={totalMembers}
        itemsPerPage={membersPerPage}
        currentPage={currentPage}
        statusLabel={
          <Trans>
            <span>
              {{ showing: !isMobile ? t('Showing') : '' } as TransInterpolation}{' '}
              <strong>{{ memberStart } as TransInterpolation}</strong> -{' '}
              <strong>{{ memberEnd } as TransInterpolation}</strong> of{' '}
              <strong>{{ totalMembers } as TransInterpolation}</strong>
            </span>
          </Trans>
        }
        onNextPageClick={onNextPageClick}
        onFirstPageClick={onFirstPageClick}
        onPreviousPageClick={onPreviousPageClick}
        onLastPageClick={onLastPageClick}
      />
    </div>
  );
};

export default UserManagementTable;
